import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Card, CardContent, CardDescription, CardHeader, CardTitle,} from "../components/card";
import {Button} from "../components/button";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from "../components/table";
import {Badge} from "../components/badge";
import {Progress} from "../components/progress";
import {AlertTriangle, Clock, Eye, Plus, TrendingUp, Users} from "lucide-react";
import {User} from "../models/User";
import {Team} from "../models/Team";
import {TeamService} from "../services/TeamService";
import {KpiService} from "../services/KpiService";
import {UserService} from "../services/UserService";
import {ClockService} from "../services/ClockService";

interface ManagerDashboardProps {
    user: User;
}

export function ManagerDashboard({user}: ManagerDashboardProps) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const [teams, setTeams] = useState<Team[]>([]);
    const [teamInfos, setTeamInfos] = useState<{ team: Team; teamHours: number; members: User[] }[]>([]);

    const [totalHoursThisWeek, setTotalHoursThisWeek] = useState(0);
    const [totalHoursWeekBefore, setTotalHoursWeekBefore] = useState(0);

    const [delaysThisWeek, setDelaysThisWeek] = useState(0);
    const [delaysWeekBefore, setDelaysWeekBefore] = useState(0);

    const [averageHoursPerEmployee, setAverageHoursPerEmployee] = useState(0);

    const kpis = [
        {
            title: "Équipes gérées",
            value: teams.length.toString(),
            icon: Users,
            color: "text-blue-600",
        },
        {
            title: "Heures totales (semaine)",
            value: `${totalHoursThisWeek.toFixed(2)}h`,
            icon: Clock,
            trend: `${totalHoursThisWeek - totalHoursWeekBefore >= 0 ? "+" : ""}${(totalHoursThisWeek - totalHoursWeekBefore).toFixed(2)}h vs semaine dernière`,
            color: "text-green-600",
        },
        {
            title: "Moyenne par employé",
            value: `${averageHoursPerEmployee.toFixed(2)}h`,
            icon: TrendingUp,
            trend: "Cible: 35h",
            color: "text-purple-600",
        },
        {
            title: "Retards cette semaine",
            value: `${delaysThisWeek}`,
            icon: AlertTriangle,
            trend: `${delaysThisWeek - delaysWeekBefore >= 0 ? "+" : ""}${delaysThisWeek - delaysWeekBefore} vs semaine dernière`,
            color: "text-orange-600",
        },
    ];

    useEffect(() => {
        const fetchTeams = async () => {
            try {
                if (!user?.id) return;
                const result = await TeamService.getTeamsByManagerId(user.id);
                setTeams(result || []);

                if (!result || result.length === 0) return;

                const currentWeekRange = ClockService.getCurrentWeekRange();
                const previousWeekRange = ClockService.getPreviousWeekRange();

                const currentKpisList = await Promise.all(
                    result.map(async (team) => {
                        const report = await KpiService.getTeamReport(
                            team.id,
                            currentWeekRange.startWeek,
                            currentWeekRange.endWeek,
                            true
                        );
                        return {
                            ...report,
                            totalUsers: team.userIds?.length || 0,
                        };
                    })
                );

                const previousKpisList = await Promise.all(
                    result.map(async (team) => {
                        const report = await KpiService.getTeamReport(
                            team.id,
                            previousWeekRange.startWeek,
                            previousWeekRange.endWeek,
                            true
                        );
                        return {
                            ...report,
                            totalUsers: team.userIds?.length || 0,
                        };
                    })
                );

                const totalCurrent = currentKpisList.reduce(
                    (acc, cur) => ({
                        totalHours: acc.totalHours + (cur.totalHours || 0),
                        totalLate: acc.totalLate + (cur.totalLate || 0),
                        totalUsers: acc.totalUsers + (cur.totalUsers || 0),
                        weeklyAverageHoursPerUser: acc.weeklyAverageHoursPerUser + (cur.weeklyAverageHoursPerUser || 0),
                    }),
                    { totalHours: 0, totalLate: 0, totalUsers: 0, weeklyAverageHoursPerUser: 0 }
                );

                const totalPrevious = previousKpisList.reduce(
                    (acc, cur) => ({
                        totalHours: acc.totalHours + (cur.totalHours || 0),
                        totalLate: acc.totalLate + (cur.totalLate || 0),
                        totalUsers: acc.totalUsers + (cur.totalUsers || 0),
                        weeklyAverageHoursPerUser: acc.weeklyAverageHoursPerUser + (cur.weeklyAverageHoursPerUser || 0),
                    }),
                    { totalHours: 0, totalLate: 0, totalUsers: 0, weeklyAverageHoursPerUser: 0 }
                );

                const now = new Date();
                const dayOfWeek = now.getDay();
                const elapsedDays = (dayOfWeek === 0 || dayOfWeek === 6) ? 5 : dayOfWeek;

                const avgHours =
                    totalCurrent.totalUsers > 0 && elapsedDays > 0
                        ? totalCurrent.totalHours / (totalCurrent.totalUsers * elapsedDays)
                        : 0;

                setTotalHoursThisWeek(totalCurrent.totalHours);
                setDelaysThisWeek(totalCurrent.totalLate);
                setAverageHoursPerEmployee(avgHours);

                setTotalHoursWeekBefore(totalPrevious.totalHours);
                setDelaysWeekBefore(totalPrevious.totalLate);
            } catch (err) {
                console.error("Erreur lors du chargement des équipes :", err);
            } finally {
                setLoading(false);
            }
        };
        fetchTeams();
    }, [user]);

    useEffect(() => {
        const fetchTeamInfos = async () => {
            try {
                const {startWeek, endWeek} = ClockService.getCurrentWeekRange();

                const infos = await Promise.all(
                    teams.map(async (team) => {
                        const teamReport = await KpiService.getTeamReport(team.id, startWeek, endWeek, true);
                        const teamHours = teamReport?.totalHours || 0;

                        const members = await UserService.getUsersByIds(team.userIds || []);

                        const membersWithKpis = await Promise.all(
                            members.map(async (member) => {
                                const userKpis = await KpiService.getUserReport(member.id, team.id, startWeek, endWeek);
                                
                                // Calculate elapsed business days for correct running average
                                const now = new Date();
                                const dayOfWeek = now.getDay(); // 0 (Sun) - 6 (Sat)
                                // If Sat/Sun, business elapsed is 5. If Mon-Fri, it's 1-5.
                                const elapsedDays = (dayOfWeek === 0 || dayOfWeek === 6) ? 5 : dayOfWeek;
                                
                                // Recalculate average daily hours based on elapsed days (frontend override)
                                if (userKpis && typeof userKpis.totalHours === 'number') {
                                    userKpis.averageDailyHours = elapsedDays > 0 ? userKpis.totalHours / elapsedDays : 0;
                                }

                                return {...member, userKpis};
                            })
                        );

                        return {team, teamHours, members: membersWithKpis};
                    })
                );

                setTeamInfos(infos);

                const sumHours = infos.reduce((acc, i) => acc + (i.teamHours || 0), 0);
                setTotalHoursThisWeek(sumHours);

                const sumLate = infos.reduce((acc, i) => {
                    return acc;
                }, 0);
            } catch (err) {
                console.error("Erreur fetchTeamInfos:", err);
            }
        };

        if (teams.length > 0) fetchTeamInfos();
    }, [teams]);

    function utcToLocal(hourStr: string | undefined): string {
        const utcDate = new Date(`1970-01-01T${hourStr}Z`);
        const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * -60000);
        return localDate.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Paris',
        });
    }


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1>Dashboard Manager</h1>
                    <p className="text-muted-foreground">
                        Vue d'ensemble de vos équipes et indicateurs clés
                    </p>
                </div>
                <Button className="gap-2" onClick={() => navigate("/register")}>
                    <Plus className="h-4 w-4"/>
                    Ajouter un employé
                </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {kpis.map((kpi, index) => {
                    const Icon = kpi.icon;
                    return (
                        <Card key={index}>
                            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                                <CardTitle className="text-sm text-muted-foreground">
                                    {kpi.title}
                                </CardTitle>
                                <Icon className={`h-4 w-4 ${kpi.color}`}/>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl">{kpi.value}</div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {kpi.trend}
                                </p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>Équipes</CardTitle>
                        <CardDescription>Vue d'ensemble de vos équipes</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {teamInfos.map(({team, teamHours}) => {
                            const activeMembers = team.userIds.length;
                            return (
                                <div key={team.id} className="space-y-2 p-4 border rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p>{team.name}</p>
                                            <p className="text-muted-foreground">
                                                {activeMembers} membre{activeMembers > 1 ? "s" : ""}
                                            </p>
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={() => navigate("/teams")}>
                                            <Eye className="h-4 w-4"/>
                                        </Button>
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Heures cette semaine</span>
                                            <span>
            {teamHours.toFixed(2)}h / {activeMembers * 35}h
          </span>
                                        </div>
                                        <Progress value={(teamHours / (activeMembers * 35)) * 100}/>
                                    </div>
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>Présence aujourd'hui</CardTitle>
                        <CardDescription>Statut actuel de vos employés</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {teamInfos.flatMap(({team, members}) =>
                            members.map((member) => {
                                const isPresent = member.userKpis?.isPresentToday || false;
                                return (
                                    <div
                                        key={`${team.id}-${member.id}`}
                                        className="flex items-center justify-between p-3 border rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                                                {member.firstName[0]}
                                                {member.lastName[0]}
                                            </div>
                                            <div>
                                                <p>
                                                    {member.firstName} {member.lastName}
                                                </p>
                                                <p className="text-muted-foreground">
                                                    {isPresent
                                                        ? `Connecté à ${utcToLocal(member.userKpis?.todayClockIn)}`
                                                        : "Non connecté"}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge variant={isPresent ? "default" : "secondary"}>
                                            {isPresent ? "Présent" : "Absent"}
                                        </Badge>
                                    </div>
                                );
                            })
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Performance des employés</CardTitle>
                    <CardDescription>
                        Heures travaillées et moyennes par employé
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employé</TableHead>
                                <TableHead>Équipe</TableHead>
                                <TableHead>Heures (semaine)</TableHead>
                                <TableHead>Moyenne/jour</TableHead>
                                <TableHead>Retards</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {teamInfos.flatMap(({team, teamHours, members}) =>
                                members.map((member) => {
                                    return (
                                        <TableRow key={`${team.id}-${member.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div
                                                        className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-sm">
                                                        {member.firstName[0]}
                                                        {member.lastName[0]}
                                                    </div>
                                                    <div>
                                                        <p>
                                                            {member.firstName} {member.lastName}
                                                        </p>
                                                        <p className="text-muted-foreground">
                                                            {member.email}
                                                        </p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            <TableCell>{team.name}</TableCell>
                                            <TableCell>{member.userKpis?.totalHours.toFixed(2) || 0}h</TableCell>
                                            <TableCell>{(member.userKpis?.averageDailyHours || 0).toFixed(2)}h</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {member.userKpis?.delays || 0}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
